/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2023, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { Inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Subject } from 'rxjs';
import { AeroDialogService } from '../../../aero/aero-dialog/aero-dialog.service';
import { TranslationService } from '../../../common/translation.service';
// eslint-disable-next-line import/no-cycle
import { DeleteAccountDialogComponent } from './delete-account-dialog.component';

/**
 * Angular service that handles Delete Account Dialog.
 */
@Injectable({
  providedIn: 'root',
})
export class DeleteAccountDialogService {

  private readonly _BULB_APP = 'https://my.bulbapp.com/account-deleted/';

  /**
   * Account id of the logged-in user.
   */
  public accountId: string;

  /**
   * Specifies whether Account has password stored in db.
   */
  public hasPasswordStored: boolean;

  /**
   * Holds value of username or password based on the account type.
   */
  public usernameOrPassword: string = '';

  /**
   * Username of the logged-in user.
   */
  public username: string;

  /**
   * Subject to listen change to perform component method
   */
  public serviceSubject = new Subject();

  /**
   * Constructor.
   *
   * @param AeroDialog Aero Dialog Service
   * @param $translate Angularjs Translation service
   * @param http Http client.
   * @param bulbErrorService Error Service
   */
  constructor(
    private AeroDialog: AeroDialogService,
    private $translate: TranslationService,
    private http: HttpClient,
    @Inject('BulbErrorService') private bulbErrorService,
  ) {
  }

  /**
   * Show Password or username for delete confirmation modal.
   *
   * @param hasPassword True if user has password
   * @param username Account username
   * @param id ID of account.
   */
  showDialog(hasPassword: boolean, username: string, id: string) {
    this.accountId = id;
    this.hasPasswordStored = hasPassword;
    let detail = this.$translate.translateInstant('account_settings_delete_account_passwordDesc');
    detail += this.$translate.translateInstant('account_settings_delete_account_permanentDelDesc');
    this.AeroDialog.showDialog({
      icon: 'far fa-trash-alt',
      iconClass: 'aero-alert',
      customContentComponent: DeleteAccountDialogComponent,
      title: `${this.$translate.translateInstant('account_settings_delete_account')}?`,
      detail,
      primaryLabel: this.$translate.translateInstant('button_delete'),
      primaryStyle: this.AeroDialog.getButtonStyleEnum().NEGATIVE_ACTION,
      closeOnPrimary: false,
      onPrimary: () => {
        this.authDeleteAccount();
      },
      data: { hasPasswordStored: hasPassword, valueChange: (value) => { this.usernameOrPassword = value; } },
    });
  }

  /**
   * Show Confirm dialog.
   */
  showConfirmDialog() {
    this.AeroDialog.showDialog({
      icon: 'far fa-trash-alt',
      iconClass: 'aero-alert',
      title: this.$translate.translateInstant('organization_turn_off_integration_modal_title'),
      detail: this.$translate.translateInstant('account_settings_delete_account_text'),
      primaryLabel: this.$translate.translateInstant('button_delete'),
      primaryStyle: this.AeroDialog.getButtonStyleEnum().NEGATIVE_ACTION,
      onPrimary: () => {
        this.deleteAccount();
      },
    });
  }

  /**
   * Handle authorization of delete account action.
   */
  authDeleteAccount(): void {
    if (this.hasPasswordStored) {
      this.http.post(`accounts/${this.accountId}/authPassword`, { password: this.usernameOrPassword })
        .toPromise()
        .then(() => {
          this.serviceSubject.next({ close: true });
          this.showConfirmDialog();
        })
        .catch((error) => {
          this.serviceSubject.next(
            {
              close: false,
              error: this.$translate.translateInstant('account_settings_delete_account_passwordIncorrect'),
            },
          );
        });
    } else if (this.usernameOrPassword.toLowerCase() === this.username.toLowerCase()) {
      this.showConfirmDialog();
    } else {
      this.serviceSubject.next(
        {
          close: false,
          error: this.$translate.translateInstant('account_settings_delete_account_usernameIncorrect'),
        },
      );
    }
  }

  /**
   * Delete account and redirect to BULB Home Page
   */
  deleteAccount() {
    this.http.delete(`accounts/${this.accountId}`)
      .toPromise()
      .then(() => {
        window.location.href = this._BULB_APP;
      })
      .catch((error) => {
        this.bulbErrorService.showError(null, error);
      });
  }
}
